'use client';

import { useCallback, useRef, useState } from 'react';
import type { ReviewFormData } from '../../model/review/interfaces';
import { toReviewSubmissionError, type ReviewSubmissionError } from '../../utils/review/submissionError';

interface UseReviewSubmissionOptions {
  formData: ReviewFormData;
  onSubmit: (formData: ReviewFormData) => Promise<void>;
  onSubmissionComplete: () => void;
}

interface UseReviewSubmissionReturn {
  handleSubmit: () => Promise<void>;
  submissionError: ReviewSubmissionError | null;
  clearSubmissionError: () => void;
  isSubmitting: boolean;
}

export const useReviewSubmission = ({
  formData,
  onSubmit,
  onSubmissionComplete,
}: UseReviewSubmissionOptions): UseReviewSubmissionReturn => {
  const [submissionError, setSubmissionError] = useState<ReviewSubmissionError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A ref, not state: two clicks in one tick both read the pre-render value and publish twice.
  const isSubmittingRef = useRef(false);

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      await onSubmit(formData);
      onSubmissionComplete();
    } catch (error) {
      // A swallowed rejection reads as "the button does nothing".
      setSubmissionError(toReviewSubmissionError(error));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [onSubmit, formData, onSubmissionComplete]);

  const clearSubmissionError = useCallback(() => setSubmissionError(null), []);

  return {
    handleSubmit,
    submissionError,
    clearSubmissionError,
    isSubmitting,
  };
};
